import IconBox from "assets/img/icon-box.svg?react"

import { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Purpose } from "config/constants"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import Checkbox from "components/Checkbox/Checkbox"
import { Flex, FlexItem } from "components/Flex"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import { Grid } from "components/Grid"
import Text from "components/Text/Text"
import { formatNumber } from "utils/utils"
import { Guid } from "@/types"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"

interface IInsertConfigurationModalProps {
    articles: { articleId: Guid, articleDescription: string, articleNumber: string, assetsCount: number }[]
    purpose?: Purpose | null
    onClose: () => void
    onSave: (params: { mergeAssets: Record<Guid, boolean>, printFeatures: Record<Guid, boolean> }) => void
}

type CommonCheckboxStatus = "empty" | "checked" | "partially"

export const InsertConfigurationModal: FC<IInsertConfigurationModalProps> = ({
    onClose,
    onSave,
    articles,
    purpose
}) => {
    const { t } = useTranslation()
    const [mergeAssetsChecked, setMergeAssetsChecked] = useState<Record<Guid, boolean>>({})
    const [printFeaturesChecked, setPrintFeaturesChecked] = useState<Record<Guid, boolean>>({})

    useEffect(() => {
        const filled = articles?.reduce((accum, item) => {
            return { ...accum, [item.articleId]: true }
        }, {}) ?? {}

        switch (purpose) {
            case (Purpose.sale):
                setPrintFeaturesChecked(filled)
                break
            case (Purpose.rent):
            case (Purpose.demo):
            case (Purpose.bridge):
            case (Purpose.internalUse):
                setMergeAssetsChecked(filled)
                break
        }
    }, [articles, purpose])

    const commonMergeAssetsStatus = useMemo<CommonCheckboxStatus>(() => {
        const keys = Object.keys(mergeAssetsChecked)
        switch (keys.length) {
            case 0: return "empty"
            case articles.length: return "checked"
            default: return "partially"
        }
    }, [articles.length, mergeAssetsChecked])

    const createMergeAssetsClickHandler = (articleId: Guid) => () => {
        setMergeAssetsChecked(prev => {
            if (!prev[articleId]) {
                return { ...prev, [articleId]: true }
            }
            const { [articleId]: _, ...others } = prev
            return others
        })
    }

    const handleCommonMergeAssetsCheckboxChange = () => {
        if (commonMergeAssetsStatus !== "empty") {
            return setMergeAssetsChecked({})
        }
        const value = articles?.reduce((accum, item) => {
            return { ...accum, [item.articleId]: true }
        }, {}) ?? {}
        setMergeAssetsChecked(value)
    }

    const commonPrintFeaturesStatus = useMemo<CommonCheckboxStatus>(() => {
        const keys = Object.keys(printFeaturesChecked)
        switch (keys.length) {
            case (0): return "empty"
            case (articles?.length): return "checked"
            default: return "partially"
        }
    }, [articles?.length, printFeaturesChecked])

    const createPrintFeaturesClickHandler = (articleId: Guid) => () => {
        setPrintFeaturesChecked(prev => {
            if (!prev[articleId]) {
                return { ...prev, [articleId]: true }
            }
            const { [articleId]: _, ...others } = prev
            return others
        })

    }

    const handleCommonPrintFeaturesCheckboxChange = () => {
        if (commonPrintFeaturesStatus !== "empty") {
            return setPrintFeaturesChecked({})
        }
        const value = articles?.reduce((accum, item) => {
            return { ...accum, [item.articleId]: true }
        }, {}) ?? {}
        setPrintFeaturesChecked(value)
    }

    const handleSave = () => {
        onSave({ mergeAssets: mergeAssetsChecked, printFeatures: printFeaturesChecked })
        onClose()
    }

    return (
        <ConfirmModal
            title={t("textModal.insertConfigurationText")}
            onClose={onClose}
            onConfirm={handleSave}
            width="952px"
            confirmButtonLabel={t("common.save")}>
            <Flex flow="column" className="border-grey-100 round">
                <Grid columns="66% 17% 17%" className="border-grey-100-between bg-grey-50">
                    <Flex className="border-right-grey-100" justifyContent="center" alignItems="center">

                    </Flex>
                    <Flex flow="column" justifyContent="center" alignItems="center" gap="1rem" className="border-right-grey-100 p-1">
                        <Text type="pSmallest" className="color-grey-500 text-center" bold>{t("textModal.mergeAssets")}</Text>
                        <Checkbox
                            partiallyChecked={commonMergeAssetsStatus === "partially"}
                            checked={commonMergeAssetsStatus === "checked"}
                            onChange={handleCommonMergeAssetsCheckboxChange}
                        />
                    </Flex>
                    <Flex flow="column" justifyContent="center" alignItems="center" gap="1rem" className="p-1">
                        <Text type="pSmallest" className="color-grey-500 text-center" bold>{t("textModal.printFeatures")}</Text>
                        <Checkbox
                            partiallyChecked={commonPrintFeaturesStatus === "partially"}
                            checked={commonPrintFeaturesStatus === "checked"}
                            onChange={handleCommonPrintFeaturesCheckboxChange}
                        />
                    </Flex>
                </Grid>
                {articles.map((item) => (
                    <Grid columns="66% 17% 17%" className="border-grey-100-between" key={item.articleId}>
                        <Flex flow="column" justifyContent="center" className="border-right-grey-100 p-1">
                            <div className="relative">
                                <ArticleDescription
                                    articleDescription={item.articleDescription}
                                    articleId={item.articleId}
                                />
                            </div>
                            <Grid alignItems="center" columns="16% auto">
                                <Text type="pSmallest" className="pr-4">
                                    <FlexibleContent text={item.articleNumber} />
                                </Text>
                                <Text type="pSmallest">
                                    <Flex alignItems="center" gap="0.8rem">
                                        <FlexItem shrink={0}>
                                            <IconBox width="18" height="18" className="d-block" />
                                        </FlexItem>
                                        {formatNumber(item.assetsCount)}
                                    </Flex>
                                </Text>
                            </Grid>
                        </Flex>
                        <Flex className="border-right-grey-100 p-1" justifyContent="center" alignItems={"center"}>
                            <Checkbox
                                checked={mergeAssetsChecked[item.articleId] ?? false}
                                onChange={createMergeAssetsClickHandler(item.articleId)}
                            />
                        </Flex>
                        <Flex justifyContent="center" className="p-1" alignItems={"center"}>
                            <Checkbox
                                checked={printFeaturesChecked[item.articleId] ?? false}
                                onChange={createPrintFeaturesClickHandler(item.articleId)}
                            />
                        </Flex>
                    </Grid>
                ))}
            </Flex>
        </ConfirmModal>
    )
}
